/**
 * Filter relaxation for "Recommended properties beyond your exact filters" (P1D-5464).
 * Pure functions only — the thunks in `data/recommendationsThunks.ts` do the I/O.
 */
import {
  MOVE_IN_TOKEN,
  asList,
  isStandardToken,
  panelPosition,
  splitToken,
  tokenKey,
} from "./filterTokens";

export const RECOMMENDATIONS_PAGE_SIZE = 10;
/** Most recommendation cards the section ever shows; paging stops once it is reached. */
export const RECOMMENDATIONS_MAX_ITEMS = 15;

export type RelaxationSortKey = "relevance_sorting_v2";

export type RelaxationStep = {
  /** 1-based index among accepted steps. */
  index: number;
  /** The tokens removed at this step: only the last-applied filter (or amenity option). */
  removedTokens: string[];
  /** count(step) − count(main list), available properties only. */
  pool: number;
  sortKey: RelaxationSortKey;
  /** The tail step: the same relaxed search's sold-out properties, shown last. */
  soldOut?: boolean;
};

/**
 * Removal sequence: last-applied first, unknown tokens after known ones in reverse panel
 * order, Move in Month always last. Only the first token is ever removed.
 */
export const buildRemovalOrder = (tokens: string[] = [], applicationOrder: string[] = []) => {
  // `available` is never relaxed — recommendations always exclude sold-out properties.
  const standard = tokens.filter((t) => isStandardToken(t) && tokenKey(t) !== AVAILABLE_KEY);
  // Applied tokens by application order, then unknown ones by panel position.
  const rank = (t: string) => {
    const i = applicationOrder.indexOf(t);
    return i === -1 ? applicationOrder.length + panelPosition(t) : i;
  };
  const reversed = [...standard].sort((a, b) => rank(a) - rank(b)).reverse();
  return [
    ...reversed.filter((t) => tokenKey(t) !== MOVE_IN_TOKEN),
    ...reversed.filter((t) => tokenKey(t) === MOVE_IN_TOKEN),
  ];
};

/** Request keys the main listing sends for each whole-filter token. */
const REMOVE_KEYS: Record<string, string[]> = {
  range_available_price: ["range_available_price", "weekly_range_price", "duration"],
  move_in_month: ["move_in_month"],
  leaseDuration: ["meta_min_lease_duration", "meta_max_lease_duration"],
  meta_types: ["room_types", "bathroom_types", "kitchen_types"],
  guarantor: ["guarantor"],
  tags: ["tags"],
  meta_unit_types: ["meta_unit_types"],
};

const AVAILABLE_KEY = "available";

/** Whether the main listing already excluded sold-out properties. */
export const excludesSoldOut = (base: Record<string, any> = {}) =>
  String(base[AVAILABLE_KEY]) === "true";

/** Keys that are never copied from the main request — the caller sets its own. */
const ALWAYS_DROP = ["p", "limit", "sort_key", "sort_order", "ids", "not_ids", "not_filter"];

/**
 * Request keys a standard filter can occupy — the only keys ever sent inside `not_filter`.
 * `available` is absent: it is a positive constraint on every step, negating it empties the page.
 */
const STANDARD_REQUEST_KEYS = [
  ...new Set([...Object.values(REMOVE_KEYS).flat(), "amenities", "bills_included"]),
];

/**
 * Copy the main listing request's params minus the removed filters. Deriving from the
 * request actually sent (not from filter state) keeps counts and pages consistent.
 */
export const relaxParams = (
  base: Record<string, any> = {},
  removedTokens: string[] = [],
  { soldOut = false } = {},
) => {
  const params: Record<string, any> = { ...base };
  ALWAYS_DROP.forEach((k) => delete params[k]);
  removedTokens.forEach((token) => {
    const [key, value] = splitToken(token);
    if (value !== undefined) {
      const rest = asList(params[key]).filter((v) => v.toLowerCase() !== value);
      if (rest.length) params[key] = rest.join(",");
      else delete params[key];
      return;
    }
    (REMOVE_KEYS[key] || [key]).forEach((k) => delete params[k]);
  });
  // Available steps only show bookable properties; the sold-out tail step only sold-out ones.
  params[AVAILABLE_KEY] = !soldOut;
  return params;
};

/**
 * Append the sold-out tail step when the relaxed search has any sold-out properties.
 * `soldOutCount` is the relaxed search's count with `available=false`.
 */
export const withSoldOutStep = (
  steps: RelaxationStep[],
  removal: string[],
  soldOutCount: number,
): RelaxationStep[] => {
  const pool = Number.isFinite(soldOutCount) ? Math.max(0, soldOutCount) : 0;
  if (!pool) return steps;
  return [
    ...steps,
    {
      index: steps.length + 1,
      removedTokens: removal,
      pool,
      sortKey: "relevance_sorting_v2",
      soldOut: true,
    },
  ];
};

/**
 * `not_filter` value for a step: the standard-filter params of the last accepted step (the main
 * request's when none is accepted yet), as a query string. java-backend negates that whole set
 * as one clause, so the page is exactly the properties the user has not seen (HLD D13).
 */
export const negationParams = (
  base: Record<string, any> = {},
  prevRemovedTokens: string[] = [],
) => {
  const relaxed = relaxParams(base, prevRemovedTokens);
  const qs = new URLSearchParams();
  STANDARD_REQUEST_KEYS.forEach((key) => {
    const value = relaxed[key];
    if (value !== undefined && value !== null && String(value) !== "") qs.set(key, String(value));
  });
  return qs.toString();
};
